import { z } from 'zod';

export const GLP1_FOLLOW_UP_SNAPSHOT_VERSION = 1;
export const GLP1_FOLLOW_UP_VISIT_SNAPSHOT_VERSION = 2;

/**
 * Machine-readable GLP-1 follow-up check-in snapshot stored in
 * FormAnswerGroup.metadata. Mirrors patient-app-api buildGlp1FollowUpSnapshot.
 */
export type Glp1FollowUpCheckInSnapshot = {
  v: typeof GLP1_FOLLOW_UP_SNAPSHOT_VERSION;
  kind: 'glp1_follow_up';
  submittedAt: string;
  answers: {
    currentWeightLbs: number;
    hasStartedMedication: boolean;
    firstDoseDate: string | null;
    giSymptoms: string[];
    symptomSeverity: string | null;
    severeAbdominalPain: boolean;
    moodChanges: boolean;
    urgentNeeds: boolean | null;
    additionalNotes: string | null;
  };
};

/**
 * Follow-up booked through the visit-reason flow. Mirrors patient-app-api
 * buildGlp1FollowUpVisitSnapshot.
 */
export type Glp1FollowUpVisitSnapshot = {
  v: typeof GLP1_FOLLOW_UP_VISIT_SNAPSHOT_VERSION;
  kind: 'glp1_follow_up';
  submittedAt: string;
  answers: {
    reason: string;
    currentWeightLbs: number;
    otherConcernsText: string | null;
    heightInches: number | null;
    bmi: number | null;
  };
};

export type Glp1FollowUpSnapshot =
  | Glp1FollowUpCheckInSnapshot
  | Glp1FollowUpVisitSnapshot;

const glp1FollowUpCheckInSnapshotSchema = z.looseObject({
  v: z.literal(GLP1_FOLLOW_UP_SNAPSHOT_VERSION),
  kind: z.literal('glp1_follow_up'),
  submittedAt: z.string(),
  answers: z.looseObject({
    currentWeightLbs: z.number(),
    hasStartedMedication: z.boolean(),
    firstDoseDate: z.string().nullable().optional().default(null),
    giSymptoms: z.array(z.string()),
    symptomSeverity: z.string().nullable().optional().default(null),
    severeAbdominalPain: z.boolean(),
    moodChanges: z.boolean(),
    urgentNeeds: z.boolean().nullable().optional().default(null),
    additionalNotes: z.string().nullable().optional().default(null),
  }),
});

const glp1FollowUpVisitSnapshotSchema = z.looseObject({
  v: z.literal(GLP1_FOLLOW_UP_VISIT_SNAPSHOT_VERSION),
  kind: z.literal('glp1_follow_up'),
  submittedAt: z.string(),
  answers: z.looseObject({
    reason: z.string(),
    currentWeightLbs: z.number(),
    otherConcernsText: z.string().nullable().optional().default(null),
    heightInches: z.number().nullable().optional().default(null),
    bmi: z.number().nullable().optional().default(null),
  }),
});

const glp1FollowUpSnapshotSchema = z.discriminatedUnion('v', [
  glp1FollowUpCheckInSnapshotSchema,
  glp1FollowUpVisitSnapshotSchema,
]);

/**
 * Parses FormAnswerGroup.metadata into a follow-up snapshot.
 * Returns null on any unexpected shape so callers fall back to HTML.
 * Extra unknown fields are allowed (forward compatibility).
 */
export function parseGlp1FollowUpSnapshot(
  metadata: string | null | undefined,
): Glp1FollowUpSnapshot | null {
  if (metadata == null || metadata === '') return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(metadata);
  } catch {
    return null;
  }

  const result = glp1FollowUpSnapshotSchema.safeParse(parsed);
  if (!result.success) return null;

  return result.data as Glp1FollowUpSnapshot;
}
